import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invitations, testSchemas, questionBanks } from '../api';
import { useToast } from '../components/Toast';
import { getInvitationStatus, formatInvitationDate } from '../utils/invitationStatus';

function AllInvitations() {
  const navigate = useNavigate();
  const showToast = useToast();
  const [invitationList, setInvitationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [schemasMap, setSchemasMap] = useState({});
  const [banksMap, setBanksMap] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      // Загружаем все данные параллельно
      const [invitationsResponse, schemasResponse, banksResponse] = await Promise.all([
        invitations.getAll(),
        testSchemas.getAll(),
        questionBanks.getAll()
      ]);

      const invitationsData = invitationsResponse.data || [];
      // Нормализуем completed к boolean для всех приглашений
      const normalizedData = invitationsData.map(inv => {
        // Принудительно конвертируем completed в boolean
        const completed = inv.completed === true || inv.completed === 1 || inv.completed === 'true' || inv.completed === '1';
        
        return {
          ...inv,
          completed: completed
        };
      });
      
      setInvitationList(normalizedData);
      
      // Создаем мапы для быстрого доступа
      const schemas = {};
      schemasResponse.data.forEach(schema => {
        schemas[schema.id] = schema;
      });
      setSchemasMap(schemas);

      const banks = {};
      banksResponse.data.forEach(bank => {
        banks[bank.id] = bank;
      });
      setBanksMap(banks);
    } catch (err) {
      console.error('Failed to load invitations', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        request: err.request,
        config: err.config
      });
      setLoadError('Ошибка загрузки приглашений: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForCSV = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusLabelForCSV = (status) => {
    const statusMap = {
      'pending': 'Ожидает',
      'completed': 'Завершен',
      'passed': 'Прошел',
      'failed': 'Не прошел',
      'deferred': 'Отложен',
      'timeout': 'Тайм-аут',
    };
    return statusMap[status] || status;
  };

  const getInvitationUrl = (code) => {
    // Генерируем ссылку с использованием домена
    const isProduction = window.location.hostname === 'skillgate.pro' || window.location.hostname === 'www.skillgate.pro';
    const baseUrl = isProduction 
      ? 'https://skillgate.pro'
      : `${window.location.protocol}//${window.location.hostname}:8000`;
    return `${baseUrl}/candidate/index.html?code=${code}`;
  };

  const copyToClipboard = (code) => {
    const url = getInvitationUrl(code);
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(() => showToast('Не удалось скопировать ссылку', 'error'));
  };

  const exportToCSV = () => {
    if (invitationList.length === 0) {
      showToast('Нет данных для выгрузки', 'error');
      return;
    }

    // Заголовки CSV
    const headers = [
      'ID приглашения',
      'Идентификатор кандидата',
      'Тест-схема',
      'База вопросов',
      'Код приглашения',
      'Дата создания',
      'Статус',
      'Завершено',
      'Дата завершения',
      'Баллы',
      'Максимальные баллы',
      'Процент выполнения',
      'Переключения фона',
      'Скриншоты',
      'Результаты оплачены',
      'Дата оплаты результатов'
    ];

    // Формируем строки данных
    const rows = invitationList.map((invitation) => {
      const schema = schemasMap[invitation.test_schema_id];
      const bank = schema ? banksMap[schema.question_bank_id] : null;
      
      const percentage = invitation.max_score > 0 
        ? ((invitation.total_score / invitation.max_score) * 100).toFixed(2) + '%'
        : '0%';

      return [
        invitation.id,
        invitation.candidate_title || '',
        schema ? schema.name : '',
        bank ? bank.name : '',
        invitation.invitation_code || '',
        formatDateForCSV(invitation.created_at),
        getStatusLabelForCSV(invitation.status || 'pending'),
        invitation.completed ? 'Да' : 'Нет',
        formatDateForCSV(invitation.completed_at),
        invitation.total_score.toFixed(2),
        invitation.max_score.toFixed(2),
        percentage,
        invitation.background_switches_count || 0,
        invitation.screenshots_count || 0,
        invitation.results_viewed ? 'Да' : 'Нет',
        formatDateForCSV(invitation.results_viewed_at)
      ];
    });

    // Функция для экранирования CSV значений
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // Если значение содержит запятую, кавычки или перенос строки, оборачиваем в кавычки
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }
      return stringValue;
    };

    // Формируем CSV содержимое
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Добавляем BOM для корректного отображения кириллицы в Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Создаем ссылку для скачивания
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Генерируем имя файла с текущей датой
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    link.setAttribute('download', `statistika_priglasheniy_${dateStr}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Загрузка приглашений...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Приглашения
          </h1>
          {invitationList.length > 0 && (
            <button
              onClick={exportToCSV}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <span>Выгрузить статистику</span>
            </button>
          )}
        </div>

        {loadError ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <span>{loadError}</span>
            <button onClick={loadData} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button>
          </div>
        ) : invitationList.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-lg">Приглашений пока нет</p>
            <button
              onClick={() => navigate('/test-schemas')}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Создать тест-схему
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Идентификатор кандидата
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Тест-схема
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      База
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дата создания
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitationList.map((invitation) => {
                    const schema = schemasMap[invitation.test_schema_id];
                    const bank = schema ? banksMap[schema.question_bank_id] : null;
                    const statusInfo = getInvitationStatus(invitation.status || 'pending');
                    
                    return (
                      <tr key={invitation.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invitation.candidate_title || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {schema ? schema.name : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {bank ? bank.name : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={statusInfo.className}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatInvitationDate(invitation.created_at)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(() => {
                              // Принудительно проверяем completed
                              const isCompleted = Boolean(invitation.completed) || invitation.completed === 1;
                              return isCompleted;
                            })() ? (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => navigate(`/test-results/${invitation.id}`)}
                                  className="bg-blue-600 text-white px-2.5 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                                >
                                  Результаты
                                </button>
                                {invitation.results_viewed === true ? (
                                  <span
                                    className="text-green-600 cursor-help text-lg font-bold"
                                    title="Результаты оплачены и доступны для просмотра"
                                  >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                                  </span>
                                ) : (
                                  <span
                                    className="text-gray-400 cursor-help text-base"
                                    title="Для просмотра результатов требуется оплата (100 руб.)"
                                  >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                  </span>
                                )}
                              </div>
                            ) : (
                              <button
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 whitespace-nowrap ${
                                  copiedCode === invitation.invitation_code
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                onClick={() => copyToClipboard(invitation.invitation_code)}
                              >
                                {copiedCode === invitation.invitation_code ? (
                                  <>
                                    <span>Скопировано</span>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <span>Ссылка-приглашение</span>
                                  </>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/test-schemas/${invitation.test_schema_id}/invitations`)}
                              className="bg-gray-500 text-white px-2.5 py-1 rounded-lg text-xs font-semibold hover:bg-gray-600 transition-colors whitespace-nowrap"
                            >
                              Подробнее
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AllInvitations;

